import React, { useEffect, useState } from 'react';
import { ScrollView } from 'react-native';
import { useAuthDispatch, useAuthState } from '../../auth/authContext';
import { COMPLETE_SIGN_UP } from '../../auth/authTypes';
import client from '../../../graphql/client';
import View from '../../components/styled/View';
import Text from '../../components/styled/Text';
import { Formik } from 'formik';
import ButtonGroup from '../../components/styled/ButtonGroup';
import ErrorText from '../../components/styled/ErrorText';
import LinearGradientButton from '../../components/styled/LinearGradientButton';
import SelectedGenresWidget from '../../components/styled/SelectedGenresWidget';
import SpotifyPreviewTrack from '../../components/styled/SpotifyPreviewTrack';
import TextInput from '../../components/styled/TextInput';
import styles from './styles';
import { createSongSchema } from './validationSchema';
import stringHelpers from '../../utils/stringHelpers';
import AppAnalytics, {
  GaCategories,
  GaActions,
} from '../../lib/analytics/Analytics';

import {
  CREATE_SESSION_TEXT,
  SESSION_EXISTS_TEXT,
  SELECT_GENRE,
  GENRE_TEXT,
  GENRE_TEXT_SELECTED,
  GENRE_SUB_TEXT_UNSELECTED,
  GENRE_SUB_TEXT_SELECTED,
} from './constants';
import SpotifyInput from '../../components/styled/SpotifyInput';

export default function CreateSessionScreen({ navigation, route }) {
  const dispatch = useAuthDispatch();
  const { cognitoId, firstTimeUser } = useAuthState();
  const [songGenre, setSongGenre] = useState<any>({ id: '', genre: '' });
  const artistGenresInput = route?.params?.artistGenres; // {id: genreId, genre: genreName}
  const updatedGenre =
    route?.params?.updatedGenre?.genre == ''
      ? null
      : route?.params?.updatedGenre;
  // TODO: REMOVE when we have a local state solution
  const allSessions = route?.params?.currentSessions || [];
  const [createSessionError, setCreateSessionError] = useState<string>('');
  const [sessionExistsError, setSessionExistsError] = useState<string>('');

  const buildCurrentSessionsObj = () => {
    return allSessions.map((session) => session.spotifyUri);
  };

  const sessionExists = (uri) => {
    const currentActiveSessions = buildCurrentSessionsObj();
    if (currentActiveSessions.includes(uri)) {
      return true;
    }
    return false;
  };

  const createSessionGenreConnection = async (sessionId) => {
    const sessionGenreObj = {
      sessionGenreSessionId: sessionId,
      sessionGenreGenreId: songGenre.id,
    };
    await client.createASessionGenre(sessionGenreObj);
  };

  const createSession = async (values) => {
    const { spotifyUri, trackName, artist } = values;
    const spotifyId = stringHelpers.formatTrackAddressToId(spotifyUri.trim());
    if (songGenre.id === '') {
      setCreateSessionError(SELECT_GENRE);
      return;
    }

    const sessionDetails = {
      isActive: true,
      spotifyUri: spotifyId,
      trackName,
      artistName: artist,
      artistId: cognitoId,
      startDate: new Date(),
      playCount: 0,
    };

    if (firstTimeUser) {
      const sessDetails = await client.createASession(sessionDetails);
      createSessionGenreConnection(sessDetails.id);
      dispatch({
        type: COMPLETE_SIGN_UP,
      });
      // TODO: REMOVE AFTER ARTIST BETA
      // Add monitoring here
    } else if (!sessionExists(spotifyId)) {
      const sessDetails = await client.createASession(sessionDetails);
      createSessionGenreConnection(sessDetails.id);
      AppAnalytics.sendEvent(GaCategories.SESSION, GaActions.CREATE, 110);
      navigation.replace('Dashboard', { isUpdated: true });
      // TODO: REMOVE AFTER ARTIST BETA
      // Add monitoring here
    } else {
      setSessionExistsError(SESSION_EXISTS_TEXT);
    }
  };

  const updateNewGenre = (selectedGenre: any) => {
    if (songGenre.id === selectedGenre.id) {
      setSongGenre({ id: '', genre: '' });
    } else {
      setSongGenre(selectedGenre);
      setCreateSessionError('');
    }
  };

  const navigateToSessionGenres = () => {
    navigation.navigate('SessionGenres', {
      songGenre: [songGenre],
    });
  };
  const goBackNavigation = () => {
    if (firstTimeUser) {
      navigation.navigate('ArtistGenres');
    } else {
      navigation.replace('Dashboard');
    }
  };

  useEffect(() => {
    if (updatedGenre) {
      updateNewGenre(updatedGenre);
    }
  }, [updatedGenre]);

  return (
    <ScrollView contentContainerStyle={styles.contentContainer}>
      <View style={styles.container}>
        <Text style={styles.title}> {CREATE_SESSION_TEXT} </Text>
        <Formik
          initialValues={{
            spotifyUri: '',
            trackName: '',
            artist: '',
          }}
          onSubmit={(values) => createSession(values)}
          validationSchema={createSongSchema}
          validateOnChange
        >
          {({
            errors,
            handleChange,
            handleBlur,
            handleSubmit,
            isValid,
            touched,
            values,
          }) => (
            <>
              <View>
                <SpotifyInput
                  errorMessage={touched.spotifyUri && errors.spotifyUri}
                  onBlur={handleBlur('spotifyUri')}
                  onChangeText={handleChange('spotifyUri')}
                  value={values.spotifyUri.trim()}
                />
                <SpotifyPreviewTrack id={values.spotifyUri.trim()} />
                <TextInput
                  autoCompleteType="off"
                  autoCapitalize="none"
                  errorMessage={touched.trackName && errors.trackName}
                  keyboardType="default"
                  label="Track Title"
                  labelHidden
                  onBlur={handleBlur('trackName')}
                  onChangeText={handleChange('trackName')}
                  placeholder="Track Title"
                  value={values.trackName}
                />
                <TextInput
                  autoCapitalize="none"
                  autoCompleteType="off"
                  errorMessage={touched.artist && errors.artist}
                  keyboardType="default"
                  label="Artist"
                  labelHidden
                  onBlur={handleBlur('artist')}
                  onChangeText={handleChange('artist')}
                  placeholder="Artist"
                  value={values.artist}
                />
                <View accessibilityLabel="checkbox-group">
                  {!updatedGenre && (
                    <SelectedGenresWidget
                      activeGenre={songGenre.id}
                      editButtonOnPress={navigateToSessionGenres}
                      genreButtonOnPress={updateNewGenre}
                      genres={artistGenresInput}
                      isDefaultGenreButtonActive={false}
                      text={GENRE_TEXT}
                      subText={GENRE_SUB_TEXT_UNSELECTED}
                    />
                  )}
                  {updatedGenre && (
                    <SelectedGenresWidget
                      activeGenre={songGenre}
                      editButtonOnPress={navigateToSessionGenres}
                      genreButtonOnPress={() => {}}
                      genres={[songGenre]}
                      isDefaultGenreButtonActive={true}
                      text={GENRE_TEXT_SELECTED}
                      subText={GENRE_SUB_TEXT_SELECTED}
                    />
                  )}
                  {!!createSessionError && (
                    <ErrorText
                      containerStyle={styles.errorContainer}
                      textStyle={styles.errorText}
                      text={createSessionError}
                    />
                  )}
                  {!!sessionExistsError && (
                    <ErrorText
                      containerStyle={styles.errorContainer}
                      textStyle={styles.errorText}
                      text={sessionExistsError}
                    />
                  )}
                </View>
                <ButtonGroup direction="row" style={styles.buttonGroup}>
                  <LinearGradientButton
                    title="Back"
                    onPress={goBackNavigation}
                  />
                  <View style={styles.nextButton}>
                    <LinearGradientButton
                      disabled={
                        !isValid ||
                        !!sessionExistsError ||
                        !!createSessionError ||
                        songGenre.id === ''
                      }
                      title="Next"
                      onPress={handleSubmit}
                    />
                  </View>
                </ButtonGroup>
              </View>
            </>
          )}
        </Formik>
      </View>
    </ScrollView>
  );
}
